import axios from "axios";
import { ProductSearchResponse, ProductDetail } from "@/types/product";
import { mockSearchResults } from "@/mocks/searchResults";
import { mockProductDetail } from "@/mocks/productDetail";
import { IndustriesResponse } from "@/types/auth";
import { store } from "@/store";
import { Brand, Category, QueryLog } from "@/types/dashboard";
import {
  updateSubscriptionInfo,
} from "@/store/slices/authSlice";
import { UploadedFile } from "@/types/uploadedFiles";
import { setBlockedFeaturesMessage } from "@/store/slices/productSlice";

const API_BASE_URL = "https://api.thmn.ai/api";
const API_LOCAL_URL = "http://192.168.1.198:8000/api";

const productsApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add interceptor to include token in requests
productsApi.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.auth.tokens.access;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Added interceptor to handle subscription and usage data
productsApi.interceptors.response.use(
  (response) => {
    if (response.data?.subscription || response.data?.subscription_usage) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: response.data.subscription || null,
          subscription_usage: response.data.subscription_usage || null,
        })
      );
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 403) {
      const blockedFeatures = error.response.data?.data?.blocked_features || 
      error.response.data?.data?.error?.detail?.blocked_features;

      if (blockedFeatures) {
        const message = Object.values(blockedFeatures)[0] as string;
        store.dispatch(setBlockedFeaturesMessage(message));
        window.location.href = "/upgrade";
      }
    }
    if (
      error.response?.data?.subscription ||
      error.response?.data?.subscription_usage
    ) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: error.response.data.subscription || null,
          subscription_usage: error.response.data.subscription_usage || null,
        })
      );
    }
    return Promise.reject(error);
  }
);

export const fetchQueryLogs = async (params: {
  page?: number;
  brand?: string;
  category?: string;
  search?: string;
}): Promise<{
  results: QueryLog[];
  count: number;
  next: string | null;
  previous: string | null;
}> => {
  const response = await productsApi.get("/query-logs/", { params });

  return response?.data?.data;
};

export const fetchCompetitorsQueryLogs = async (params: {
  page?: number;
  brand?: string;
  category?: string;
  search?: string;
  provider_ids?: string;
}): Promise<{
  results: QueryLog[];
  count: number;
  next: string | null;
  previous: string | null;
}> => {
  const response = await productsApi.get("/query-logs/competitors/", {
    params,
  });

  return response?.data?.data;
};

export const fetchBrands = async (params: {
  page?: number;
  page_size?: number;
}): Promise<{
  results: Brand[];
  next: string | null;
}> => {
  const response = await productsApi.get("/products/brands/", { params });
  return response?.data?.data;
};

export const fetchCategories = async (params: {
  page?: number;
  page_size?: number;
}): Promise<{
  results: Category[];
  next: string | null;
}> => {
  const response = await productsApi.get("/products/categories/", { params });
  return response?.data?.data;
};

export const fetchIndustries = async (): Promise<IndustriesResponse> => {
  try {
    const response = await productsApi.get("/companies/industries/");
    return response?.data?.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error("Failed to fetch industries");
    }
    throw error;
  }
};

export const searchProducts = async ({
  query,
  pageParam = 1,
  pageSize = 10,
}: {
  query: string;
  pageParam?: number;
  pageSize?: number;
}): Promise<ProductSearchResponse> => {
  const response = await productsApi.get("/products/search/", {
    params: {
      page: pageParam,
      page_size: pageSize,
      query: query,
    },
  });
  return response?.data?.data;
};

export const getProductDetail = async (
  productId: string
): Promise<ProductDetail> => {
  const response = await productsApi.get(`/products/${productId}/`);
  return response?.data?.data;
};

export const trackProduct = async (productId: string): Promise<void> => {
  const response = await productsApi.post(`/products/${productId}/track/`);
  if (response.status !== 202) {
    throw new Error("Failed to track product");
  }
};

export const getUploadFile = async (): Promise<UploadedFile[]> => {
  const response = await productsApi.get(`/file-uploads/`);
  return response?.data?.data;
};

export const exportDashboard = async (params: {
  page?: number;
  brand?: string;
  category?: string;
  search?: string;
}) => {
  const response = await productsApi.get("/query-logs/export/", {
    params,
  });

  return response?.data?.data;
};

export const exportCompetitorsDashboard = async (params: {
  page?: number;
  brand?: string;
  category?: string;
  search?: string;
  provider_ids?: string;
}) => {
  const response = await productsApi.get("/query-logs/competitors-export/", {
    params,
  });

  return response?.data?.data;
};